import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `GET /account/subaccount/permissions` sends each permission's `Label` as raw English in every locale, and
 * each section only as a kebab-case key (`SectionName` is vopay's English title-cased fallback of it) — so
 * the sub-account permissions drawer rendered English names under French headings. Per the i18n-conventions
 * "API-derived labels that double as logic keys" rule, the raw values stay the logic keys (`Permission` is
 * the form field name, `SectionId` the grouping key, and the dollar-under-toggle pairing matches on the raw
 * `Label` in `adaptAccountPermissions`) and only the rendered string is localized, here.
 *
 * Keyed on the `Permission` code and the `SectionId`, never on the English prose. The English
 * `defaultValue`s are exactly what the API renders today, so the English screen is unchanged. The codes are
 * plain `string`s in `vopay`, so nothing fails when the backend adds one: an unmapped permission renders the
 * API's own `Label` and an unmapped section its `SectionName` — the pre-i18n behaviour, never blank. Opening
 * the drawer in French is what finds one.
 *
 * Memoized on `t`, whose identity changes only with the language, so callers can list the resolvers in a
 * `useMemo` dependency array.
 */
export default function useSubAccountPermissionLabels() {
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseSubAccountPermissionLabels' });

  return useMemo(() => {
    const labels: Record<string, string> = {
      EFTSendEnabled: t('Labels.eftSendEnabled', { defaultValue: 'EFT Send' }),
      MaxWithdrawEFTAmount: t('Labels.maxWithdrawEFTAmount', { defaultValue: 'Max Withdraw EFT Amount' }),
      USDEFTSendEnabled: t('Labels.usdEFTSendEnabled', { defaultValue: 'USD EFT Send' }),
      RTRWithdrawEnabled: t('Labels.rtrWithdrawEnabled', { defaultValue: 'VoPay Instant™ - Send' }),
      MaxRTRWithdrawAmount: t('Labels.maxRTRWithdrawAmount', { defaultValue: 'Max VoPay Instant™ - Send Amount' }),
      ACHWithdrawEnabled: t('Labels.achWithdrawEnabled', { defaultValue: 'ACH Withdraw' }),
      MaxACHWithdrawAmount: t('Labels.maxACHWithdrawAmount', { defaultValue: 'Max ACH Withdraw Amount' }),
      SameDayACHWithdrawEnabled: t('Labels.sameDayACHWithdrawEnabled', { defaultValue: 'Same Day ACH Withdraw' }),
      SameDayMaxACHWithdrawAmount: t('Labels.sameDayMaxACHWithdrawAmount', {
        defaultValue: 'Max Same Day ACH Withdraw Amount',
      }),
      FednowWithdrawEnabled: t('Labels.fednowWithdrawEnabled', { defaultValue: 'Fednow Withdraw' }),
      MaxFednowWithdrawAmount: t('Labels.maxFednowWithdrawAmount', { defaultValue: 'Max Fednow Withdraw Amount' }),
      RTPWithdrawEnabled: t('Labels.rtpWithdrawEnabled', { defaultValue: 'RTP Withdraw' }),
      MaxRTPWithdrawAmount: t('Labels.maxRTPWithdrawAmount', { defaultValue: 'Max RTP Withdraw Amount' }),
      WireEnabled: t('Labels.wireEnabled', { defaultValue: 'Fedwire Withdraw' }),
      MaxWireAmount: t('Labels.maxWireAmount', { defaultValue: 'Max Fedwire Withdraw Amount' }),
      DailyLimitOutboundBankAccount: t('Labels.dailyLimitOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Daily Limit',
      }),
      DailyLimitAmountOutboundBankAccount: t('Labels.dailyLimitAmountOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Daily Limit Amount',
      }),
      WeeklyLimitOutboundBankAccount: t('Labels.weeklyLimitOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Weekly Limit',
      }),
      WeeklyLimitAmountOutboundBankAccount: t('Labels.weeklyLimitAmountOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Weekly Limit Amount',
      }),
      MonthlyLimitOutboundBankAccount: t('Labels.monthlyLimitOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Monthly Limit',
      }),
      MonthlyLimitAmountOutboundBankAccount: t('Labels.monthlyLimitAmountOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Monthly Limit Amount',
      }),
      InteracBulkPayoutEnabled: t('Labels.interacBulkPayoutEnabled', { defaultValue: 'Interac e-Transfer® Send' }),
      MaxInteracBulkPayoutAmount: t('Labels.maxInteracBulkPayoutAmount', {
        defaultValue: 'Max Interac e-Transfer® Send Amount',
      }),
      PaypalWithdrawEnabled: t('Labels.paypalWithdrawEnabled', { defaultValue: 'PayPal Withdraw' }),
      VenmoWithdrawEnabled: t('Labels.venmoWithdrawEnabled', { defaultValue: 'Venmo Withdraw' }),
      DailyLimitOutboundEmailAddress: t('Labels.dailyLimitOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Daily Limit',
      }),
      WeeklyLimitOutboundEmailAddress: t('Labels.weeklyLimitOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Weekly Limit',
      }),
      MonthlyLimitOutboundEmailAddress: t('Labels.monthlyLimitOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Monthly Limit',
      }),
      DailyLimitAmountOutboundEmailAddress: t('Labels.dailyLimitAmountOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Daily Limit Amount',
      }),
      WeeklyLimitAmountOutboundEmailAddress: t('Labels.weeklyLimitAmountOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Weekly Limit Amount',
      }),
      MonthlyLimitAmountOutboundEmailAddress: t('Labels.monthlyLimitAmountOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Monthly Limit Amount',
      }),
      GCMEnabled: t('Labels.gcmEnabled', { defaultValue: 'Global Send' }),
      MaxGCMAmount: t('Labels.maxGCMAmount', { defaultValue: 'Max Global Send Amount' }),
      PushToCardEnabled: t('Labels.pushToCardEnabled', { defaultValue: 'Debit Card - Push to Card' }),
      MaxPushToCardAmount: t('Labels.maxPushToCardAmount', { defaultValue: 'Max Debit Card - Push to Card Amount' }),
      DailyLimitOutboundDebitCard: t('Labels.dailyLimitOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Daily Limit',
      }),
      DailyLimitAmountOutboundDebitCard: t('Labels.dailyLimitAmountOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Daily Limit Amount',
      }),
      WeeklyLimitOutboundDebitCard: t('Labels.weeklyLimitOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Weekly Limit',
      }),
      WeeklyLimitAmountOutboundDebitCard: t('Labels.weeklyLimitAmountOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Weekly Limit Amount',
      }),
      MonthlyLimitOutboundDebitCard: t('Labels.monthlyLimitOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Monthly Limit',
      }),
      MonthlyLimitAmountOutboundDebitCard: t('Labels.monthlyLimitAmountOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Monthly Limit Amount',
      }),
      BillPayEnabled: t('Labels.billPayEnabled', { defaultValue: 'Bill Pay' }),
      MaxBillPayAmount: t('Labels.maxBillPayAmount', { defaultValue: 'Max Bill Pay Amount' }),
      DailyLimitOutboundAmount: t('Labels.dailyLimitOutboundAmount', { defaultValue: 'Outbound Daily Limit Amount' }),
      WeeklyLimitOutboundAmount: t('Labels.weeklyLimitOutboundAmount', {
        defaultValue: 'Outbound Weekly Limit Amount',
      }),
      MonthlyLimitOutboundAmount: t('Labels.monthlyLimitOutboundAmount', {
        defaultValue: 'Outbound Monthly Limit Amount',
      }),
      MonthlyOutboundTransactionLimit: t('Labels.monthlyOutboundTransactionLimit', {
        defaultValue: 'Outbound Monthly Transaction Limit',
      }),
      EFTCollectEnabled: t('Labels.eftCollectEnabled', { defaultValue: 'EFT Collect' }),
      MaxFundEFTAmount: t('Labels.maxFundEFTAmount', { defaultValue: 'Max Fund EFT Amount' }),
      InboundEtransferEnabled: t('Labels.inboundEtransferEnabled', { defaultValue: 'Inbound Etransfer' }),
      USDEFTCollectEnabled: t('Labels.usdEFTCollectEnabled', { defaultValue: 'USD EFT Collect' }),
      ACHFundEnabled: t('Labels.achFundEnabled', { defaultValue: 'ACH Fund' }),
      MaxACHFundAmount: t('Labels.maxACHFundAmount', { defaultValue: 'Max ACH Fund Amount' }),
      SameDayACHFundEnabled: t('Labels.sameDayACHFundEnabled', { defaultValue: 'Same Day ACH Fund' }),
      SameDayMaxACHFundAmount: t('Labels.sameDayMaxACHFundAmount', { defaultValue: 'Max Same Day ACH Fund Amount' }),
      FedwireFundEnabled: t('Labels.fedwireFundEnabled', { defaultValue: 'Fedwire Fund' }),
      MaxFedwireFundAmount: t('Labels.maxFedwireFundAmount', { defaultValue: 'Max Fedwire Fund Amount' }),
      DailyLimitInboundBankAccount: t('Labels.dailyLimitInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Daily Limit',
      }),
      DailyLimitAmountInboundBankAccount: t('Labels.dailyLimitAmountInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Daily Limit Amount',
      }),
      WeeklyLimitInboundBankAccount: t('Labels.weeklyLimitInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Weekly Limit',
      }),
      WeeklyLimitAmountInboundBankAccount: t('Labels.weeklyLimitAmountInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Weekly Limit Amount',
      }),
      MonthlyLimitInboundBankAccount: t('Labels.monthlyLimitInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Monthly Limit',
      }),
      MonthlyLimitAmountInboundBankAccount: t('Labels.monthlyLimitAmountInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Monthly Limit Amount',
      }),
      InteracMoneyRequestEnabled: t('Labels.interacMoneyRequestEnabled', {
        defaultValue: 'Interac e-Transfer® Request Money',
      }),
      MaxInteracMoneyAmount: t('Labels.maxInteracMoneyAmount', { defaultValue: 'Max Interac Money Amount' }),
      MoneyRequestGenerateUrlEnabled: t('Labels.moneyRequestGenerateUrlEnabled', {
        defaultValue: 'Interac e-Transfer® Request Money - Embedded',
      }),
      DailyLimitInboundEmailAddress: t('Labels.dailyLimitInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Daily Limit',
      }),
      WeeklyLimitInboundEmailAddress: t('Labels.weeklyLimitInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Weekly Limit',
      }),
      MonthlyLimitInboundEmailAddress: t('Labels.monthlyLimitInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Monthly Limit',
      }),
      DailyLimitAmountInboundEmailAddress: t('Labels.dailyLimitAmountInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Daily Limit Amount',
      }),
      WeeklyLimitAmountInboundEmailAddress: t('Labels.weeklyLimitAmountInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Weekly Limit Amount',
      }),
      MonthlyLimitAmountInboundEmailAddress: t('Labels.monthlyLimitAmountInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Monthly Limit Amount',
      }),
      AFTEnabled: t('Labels.aftEnabled', { defaultValue: 'Debit Card - Pull from Card' }),
      MaxAFTAmount: t('Labels.maxAFTAmount', { defaultValue: 'Max Debit Card - Pull from Card Amount' }),
      DailyLimitInboundDebitCard: t('Labels.dailyLimitInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Daily Limit',
      }),
      DailyLimitAmountInboundDebitCard: t('Labels.dailyLimitAmountInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Daily Limit Amount',
      }),
      WeeklyLimitInboundDebitCard: t('Labels.weeklyLimitInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Weekly Limit',
      }),
      WeeklyLimitAmountInboundDebitCard: t('Labels.weeklyLimitAmountInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Weekly Limit Amount',
      }),
      MonthlyLimitInboundDebitCard: t('Labels.monthlyLimitInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Monthly Limit',
      }),
      MonthlyLimitAmountInboundDebitCard: t('Labels.monthlyLimitAmountInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Monthly Limit Amount',
      }),
      CreditCardEnabled: t('Labels.creditCardEnabled', { defaultValue: 'Credit Card' }),
      DailyLimitInboundCreditCard: t('Labels.dailyLimitInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Daily Limit',
      }),
      DailyLimitAmountInboundCreditCard: t('Labels.dailyLimitAmountInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Daily Limit Amount',
      }),
      WeeklyLimitInboundCreditCard: t('Labels.weeklyLimitInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Weekly Limit',
      }),
      WeeklyLimitAmountInboundCreditCard: t('Labels.weeklyLimitAmountInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Weekly Limit Amount',
      }),
      MonthlyLimitInboundCreditCard: t('Labels.monthlyLimitInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Monthly Limit',
      }),
      MonthlyLimitAmountInboundCreditCard: t('Labels.monthlyLimitAmountInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Monthly Limit Amount',
      }),
      GooglePayEnabled: t('Labels.googlePayEnabled', { defaultValue: 'Google Pay' }),
      ApplePayEnabled: t('Labels.applePayEnabled', { defaultValue: 'Apple Pay' }),
      DailyLimitInboundAmount: t('Labels.dailyLimitInboundAmount', { defaultValue: 'Inbound Daily Limit Amount' }),
      WeeklyLimitInboundAmount: t('Labels.weeklyLimitInboundAmount', { defaultValue: 'Inbound Weekly Limit Amount' }),
      MonthlyLimitInboundAmount: t('Labels.monthlyLimitInboundAmount', {
        defaultValue: 'Inbound Monthly Limit Amount',
      }),
      MonthlyInboundTransactionLimit: t('Labels.monthlyInboundTransactionLimit', {
        defaultValue: 'Inbound Monthly Transaction Limit',
      }),
      FundMyAccountEnabled: t('Labels.fundMyAccountEnabled', { defaultValue: 'Fund My Account' }),
      FundMyAccountCreditCardEnabled: t('Labels.fundMyAccountCreditCardEnabled', {
        defaultValue: 'Fund My Account - Credit Card',
      }),
      MaxFundMyAccountAmount: t('Labels.maxFundMyAccountAmount', { defaultValue: 'Max Fund My Account Amount' }),
      WithdrawMyAccountEnabled: t('Labels.withdrawMyAccountEnabled', { defaultValue: 'Withdraw My Account' }),
      MaxWithdrawMyAccountAmount: t('Labels.maxWithdrawMyAccountAmount', {
        defaultValue: 'Max Withdraw My Account Amount',
      }),
      AccountTransferEnabled: t('Labels.accountTransferEnabled', { defaultValue: 'Account Transfer' }),
      AutoBalanceTransferEnabled: t('Labels.autoBalanceTransferEnabled', { defaultValue: 'Auto Balance Transfer' }),
      AutoBalanceTransferAmount: t('Labels.autoBalanceTransferAmount', {
        defaultValue: 'Auto Balance Transfer Amount',
      }),
      FlagDuplicateTransactions: t('Labels.flagDuplicateTransactions', { defaultValue: 'Flag Duplicate Transactions' }),
      MicroDepositBankAccountVerificationEnabled: t('Labels.microDepositBankAccountVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Bank Verification (Micro Deposits)',
      }),
      CardBasicVerificationEnabled: t('Labels.cardBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic Card Verification',
      }),
      CardEnhancedVerificationEnabled: t('Labels.cardEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced Card Verification',
      }),
      InteracBasicVerificationEnabled: t('Labels.interacBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic Interac® Verification (Email)',
      }),
      InteracEnhancedVerificationEnabled: t('Labels.interacEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced Interac® Verification (Email)',
      }),
      EmailAddressBasicVerificationEnabled: t('Labels.emailAddressBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic Email Verification',
      }),
      EmailAddressEnhancedVerificationEnabled: t('Labels.emailAddressEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced Email Verification',
      }),
      PhoneNumberBasicVerificationEnabled: t('Labels.phoneNumberBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic Phone Number Verification',
      }),
      PhoneNumberEnhancedVerificationEnabled: t('Labels.phoneNumberEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced Phone Number Verification',
      }),
      DualProcessVerificationEnabled: t('Labels.dualProcessVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Non-doc ID Verification',
      }),
      AddressVerificationEnabled: t('Labels.addressVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Address Verification',
      }),
      IdBasicVerificationEnabled: t('Labels.idBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic ID Verification',
      }),
      IdEnhancedVerificationEnabled: t('Labels.idEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced ID Verification',
      }),
      NameScreenVerificationEnabled: t('Labels.nameScreenVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Name Screen Verification',
      }),
      BrandingEnabled: t('Labels.brandingEnabled', { defaultValue: 'Branding' }),
      BrandingEmailEnabled: t('Labels.brandingEmailEnabled', { defaultValue: 'Email Branding' }),
      BrandingELinxEnabled: t('Labels.brandingELinxEnabled', { defaultValue: 'eLinx™ Branding' }),
      BrandingPortalEnabled: t('Labels.brandingPortalEnabled', { defaultValue: 'Account Portal Branding' }),
      BrandingOnboardingEnabled: t('Labels.brandingOnboardingEnabled', { defaultValue: 'Onboarding Form Branding' }),
      TransactionApprovalWorkflowEnabled: t('Labels.transactionApprovalWorkflowEnabled', {
        defaultValue: 'Transaction Approval Workflow',
      }),
      AccountingSoftwareIntegrationEnabled: t('Labels.accountingSoftwareIntegrationEnabled', {
        defaultValue: 'VoPay 360 - Accounting Software Integration',
      }),
      PayLinkEnabled: t('Labels.payLinkEnabled', { defaultValue: 'eLinx™' }),
      IQ11Enabled: t('Labels.iq11Enabled', { defaultValue: 'IQ11' }),
      JournalEntryEnabled: t('Labels.journalEntryEnabled', { defaultValue: 'Journal Entry' }),
      TransactionLabelOverrideEnabled: t('Labels.transactionLabelOverrideEnabled', {
        defaultValue: 'Transaction Label Override',
      }),
    };

    const sections: Record<string, string> = {
      'payment-rails': t('Sections.paymentRails', { defaultValue: 'Payment Rails' }),
      send: t('Sections.send', { defaultValue: 'Send' }),
      'bank-account': t('Sections.bankAccount', { defaultValue: 'Bank Account' }),
      'bank-payment-rails': t('Sections.bankPaymentRails', { defaultValue: 'Bank Payment Rails' }),
      velocities: t('Sections.velocities', { defaultValue: 'Velocities' }),
      email: t('Sections.email', { defaultValue: 'Email' }),
      'email-rails': t('Sections.emailRails', { defaultValue: 'Email Rails' }),
      'global-send': t('Sections.globalSend', { defaultValue: 'Global Send' }),
      'global-send-rails': t('Sections.globalSendRails', { defaultValue: 'Global Send Rails' }),
      'debit-card': t('Sections.debitCard', { defaultValue: 'Debit Card' }),
      'debit-card-rails': t('Sections.debitCardRails', { defaultValue: 'Debit Card Rails' }),
      'debit-card-velocities': t('Sections.debitCardVelocities', { defaultValue: 'Debit Card Velocities' }),
      'bill-pay': t('Sections.billPay', { defaultValue: 'Bill Pay' }),
      'bill-pay-rails': t('Sections.billPayRails', { defaultValue: 'Bill Pay Rails' }),
      'global-velocities': t('Sections.globalVelocities', { defaultValue: 'Global Velocities' }),
      collect: t('Sections.collect', { defaultValue: 'Collect' }),
      'credit-card': t('Sections.creditCard', { defaultValue: 'Credit Card' }),
      'credit-card-rails': t('Sections.creditCardRails', { defaultValue: 'Credit Card Rails' }),
      'credit-card-velocities': t('Sections.creditCardVelocities', { defaultValue: 'Credit Card Velocities' }),
      'digital-wallets': t('Sections.digitalWallets', { defaultValue: 'Digital Wallets' }),
      'digital-wallets-rails': t('Sections.digitalWalletsRails', { defaultValue: 'Digital Wallets Rails' }),
      'digital-wallets-velocities': t('Sections.digitalWalletsVelocities', {
        defaultValue: 'Digital Wallets Velocities',
      }),
      'my-account': t('Sections.myAccount', { defaultValue: 'My Account' }),
      'account-configuration': t('Sections.accountConfiguration', { defaultValue: 'Account Configuration' }),
      'account-transfer': t('Sections.accountTransfer', { defaultValue: 'Account Transfer' }),
      'account-balance-transfer': t('Sections.accountBalanceTransfer', { defaultValue: 'Account Balance Transfer' }),
      flags: t('Sections.flags', { defaultValue: 'Flags' }),
      'vopay-verify': t('Sections.vopayVerify', { defaultValue: 'Vopay Verify' }),
      'payment-method-verification': t('Sections.paymentMethodVerification', {
        defaultValue: 'Payment Method Verification',
      }),
      'contact-verification': t('Sections.contactVerification', { defaultValue: 'Contact Verification' }),
      'branding-settings': t('Sections.brandingSettings', { defaultValue: 'Branding Settings' }),
      'additional-setting': t('Sections.additionalSetting', { defaultValue: 'Additional Setting' }),
    };

    return {
      resolveItemLabel: (code: string, rawLabel: string) => labels[code] ?? rawLabel,
      resolveSectionLabel: (sectionID: string, rawLabel: string) => sections[sectionID] ?? rawLabel,
    };
  }, [t]);
}
